/**
 * fontResourceAdapter.js — resource-registry adapter for Font Resources
 * (Phase 4, see docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md).
 *
 * Same interface as datasetResourceAdapter.js. isUsed() delegates to the
 * generic, resourceType-agnostic `isResourceUsed()` in
 * src/utils/resourceUsage.js (shared with imageResourceAdapter.js) — but
 * matched against `file`, not `uuid`: a widget's `font` property holds the
 * literal wire-format string (this resource's on-device `file` path for a
 * project font), not a Designer uuid — see label.js's `font` property
 * comment.
 */

import { useFontStore } from '../fontStore.js'
import { isResourceUsed } from '../../utils/resourceUsage.js'
import { addFontFile, getFontFile } from '../../services/resourceStorage.js'
import { base64ToText } from '../../package/base64.js'

/**
 * Package entries always carry this adapter's JSON envelope as text, but a
 * source (GitHub blobs, in particular) may hand it back base64-encoded
 * regardless of its semantic content — decode before parsing either way.
 */
function parseEnvelope(entry) {
  const text = entry.encoding === 'base64' ? base64ToText(entry.content) : entry.content
  return JSON.parse(text)
}

export const fontResourceAdapter = {
  type: 'font',
  // See imageResourceAdapter.js's `packageFolder` comment — same contract.
  packageFolder: 'fonts',

  list() {
    return useFontStore().all
  },
  get(uuid) {
    return useFontStore().getFont(uuid)
  },
  remove(uuid) {
    return useFontStore().removeFont(uuid)
  },
  duplicate(uuid) {
    return useFontStore().duplicateFont(uuid)
  },
  rename(uuid, name) {
    return useFontStore().updateFont(uuid, { name })
  },
  isUsed(uuid, layer) {
    const font = useFontStore().getFont(uuid)
    return !!font?.file && isResourceUsed(layer, 'font', font.file)
  },
  getDisplayName(uuid) {
    return useFontStore().getFont(uuid)?.name ?? null
  },

  // --- Packaging (Design Packages — see docs plan) ---

  /**
   * One font = one file in fonts/: a JSON envelope (`file`/`name` — the wire
   * identity, which the raw bytes alone don't carry — alongside the font's
   * own data-URL bytes), not raw binary like images. Unlike images, a font's
   * dedup/export identity (`file`) isn't recoverable from a filename alone,
   * so it has to travel with the bytes.
   */
  async toPackageFile(uuid) {
    const font = useFontStore().getFont(uuid)
    if (!font?.storageId) return null
    const record = await getFontFile(font.storageId)
    if (!record) return null
    const base = (font.originalFilename || font.id || 'font').replace(/\.[^./]+$/, '')
    return {
      filename: `${base}.font.json`,
      encoding: 'utf8',
      mime: 'application/json',
      content: JSON.stringify({
        file: font.file,
        name: font.name,
        originalFilename: font.originalFilename,
        type: font.type,
        metadata: font.metadata,
        data: record.data, // font bytes, as the same data-URL FontDB already stores
      }),
    }
  },
  validatePackageFile(entry) {
    let parsed
    try {
      parsed = parseEnvelope(entry)
    } catch {
      throw new Error(`Font "${entry.filename}" is not valid`)
    }
    if (!parsed?.data || !/^data:/.test(parsed.data)) {
      throw new Error(`Font "${entry.filename}" has no font data`)
    }
  },
  async commitPackageFile(entry) {
    const store = useFontStore()
    const parsed = parseEnvelope(entry)
    const existing = store.all.find(f =>
      (parsed.file && f.file === parsed.file) ||
      (parsed.originalFilename && f.originalFilename === parsed.originalFilename)
    )
    if (existing) return { uuid: existing.uuid, created: false }

    const uuid = store.addFont()
    const storageId = await addFontFile(parsed.data, parsed.originalFilename, parsed.type)
    store.updateFont(uuid, {
      name: parsed.name || 'Imported Font',
      file: parsed.file || '',
      storageId,
      originalFilename: parsed.originalFilename ?? null,
      type: parsed.type ?? null,
      metadata: parsed.metadata ?? {},
    })
    return { uuid, created: true }
  },
}
